import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { createPortal } from "react-dom";
import { CloseOnBack } from "../../../components/common/useCloseOnBack";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../supabase";
import { FiX, FiCalendar, FiTag, FiGift, FiChevronRight, FiCreditCard, FiStar, FiArchive, FiInfo, FiLock } from "react-icons/fi";
import BottomSheet from "../../../components/common/BottomSheet";
import "./Coupons.css";
import { useParkSettings } from "../../../utils/parkSettings";

const WEEKDAY_OPTIONS = [
  { value: 'monday', labelKey: 'weekdayMonday' },
  { value: 'tuesday', labelKey: 'weekdayTuesday' },
  { value: 'wednesday', labelKey: 'weekdayWednesday' },
  { value: 'thursday', labelKey: 'weekdayThursday' },
  { value: 'friday', labelKey: 'weekdayFriday' },
  { value: 'saturday', labelKey: 'weekdaySaturday' },
  { value: 'sunday', labelKey: 'weekdaySunday' },
];
const DAY_MS = 24 * 60 * 60 * 1000;
const USED_COUPON_RETENTION_DAYS = 14;


function getCouponStatus(t, coupon, isUsed) {
  const isUnlimited = coupon.unlimited || coupon.expiration === null || !coupon.expiration;
  if (isUsed && !isUnlimited) return { text: t('statusUsed'), cardCls: 'used-tag', tagCls: 'gray' };
  if (!isUnlimited) {
    const [y, m, d] = coupon.expiration.split('-');
    const expDate = new Date(y, m - 1, d, 23, 59, 59);
    const daysLeft = (expDate - Date.now()) / 86400000;
    if (daysLeft <= 7) return { text: t('statusExpiringSoon'), cardCls: 'expiring', tagCls: 'orange' };
  }
  return { text: t('statusActive'), cardCls: 'active', tagCls: 'green' };
}

function getTodayWeekdayKey() {
  const day = new Date().getDay();
  const map = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return map[day];
}

function isCouponAllowedToday(coupon) {
  if (!coupon?.repeatWeekly) return true;
  const repeatDays = Array.isArray(coupon.repeatDays) ? coupon.repeatDays : [];
  if (repeatDays.length === 0) return false;
  return repeatDays.includes(getTodayWeekdayKey());
}

// "Ubegrenset bruk" (unlimited) on a reward means unlimited USES too, not just no
// expiration date - the checkbox label already says so, the max-uses cap must respect it.
function getRewardMaxUses(reward) {
  if (reward?.unlimited) return Infinity;
  return Math.max(1, Number.parseInt(reward?.maxUses, 10) || 1);
}

// A reward grant tracks two separate counts so buying it and showing it to staff don't
// collide into one number: usageCount = how many times paid for with points (capped by
// maxUses, see handleRedeemReward), claimCount = how many of those purchases have
// actually been shown to staff via the same "Vis til ansatt" flow coupons use
// (markCouponAsUsed) - a reward is only "Brukt" once every purchase has been claimed.
function isRewardGrant(c) {
  return Boolean(c?.redeemedWithPoints);
}
function getRewardPurchaseCount(c) {
  return Number.parseInt(c?.usageCount, 10) || 0;
}
function getRewardClaimCount(c) {
  return Number.parseInt(c?.claimCount, 10) || 0;
}

// Flattens a `coupons_catalog` row (snake_case) to the camelCase shape this
// component already expects (matches the old Firestore myCouponsFb doc).
function toCatalogShape(row) {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    pointCost: row.point_cost,
    unlimited: row.unlimited,
    expiration: row.expiration,
    maxUses: row.max_uses,
    repeatWeekly: row.repeat_weekly,
    repeatDays: row.repeat_days,
    priceBadge: row.price_badge,
    startTime: row.start_time,
    endTime: row.end_time,
    deleted: row.deleted,
  };
}

// Flattens a `user_coupons` row (with its embedded coupons_catalog join) into
// the same merged camelCase shape the original Firestore grant doc had -
// there, the grant doc's own ID *was* the catalog coupon's ID, so `coupon.id`
// throughout this component means the catalog ID, not the grant row's own ID
// (kept here as `grantId` for the update calls that need it).
function toCouponShape(row) {
  return {
    ...toCatalogShape(row.coupons_catalog || {}),
    grantId: row.id,
    used: row.used,
    usageCount: row.usage_count,
    claimCount: row.claim_count,
    redeemedWithPoints: row.redeemed_with_points,
    redeemedAt: row.redeemed_at,
    usedAt: row.used_at,
    createdAt: row.created_at,
    deleted: row.deleted,
  };
}

function getUsedAtMs(coupon) {
  if (!coupon) return null;
  const usedAt = coupon.usedAt;

  if (typeof usedAt === 'string') {
    const parsed = Date.parse(usedAt);
    return Number.isNaN(parsed) ? null : parsed;
  }

  if (typeof usedAt?.toDate === 'function') {
    const parsedDate = usedAt.toDate();
    return parsedDate instanceof Date ? parsedDate.getTime() : null;
  }

  if (usedAt instanceof Date) return usedAt.getTime();
  return null;
}

function getUsedCouponRetention(coupon) {
  const usedAtMs = getUsedAtMs(coupon);
  if (!usedAtMs) {
    return { shouldShow: true, daysLeft: USED_COUPON_RETENTION_DAYS };
  }

  const removeAt = usedAtMs + (USED_COUPON_RETENTION_DAYS * DAY_MS);
  const msLeft = removeAt - Date.now();

  if (msLeft <= 0) return { shouldShow: false, daysLeft: 0 };
  return { shouldShow: true, daysLeft: Math.max(1, Math.ceil(msLeft / DAY_MS)) };
}

// Cached alongside Home/Profile's pws_user_cache + pws_auth flag, so a repeat visit
// paints the last-known coupons/rewards instantly instead of skeleton-flashing every
// time while the auth check + Firestore snapshot round-trip again in the background.
const getCachedCoupons = () => {
  try { return JSON.parse(localStorage.getItem('pws_coupons_cache')); } catch { return null; }
};

export default function Coupons() {
  const { t } = useTranslation('coupons');
  const { pointsPercent } = useParkSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cachedCoupons = getCachedCoupons();
  const hasAuthFlag = localStorage.getItem('pws_auth') === '1';
  const canUseCache = Boolean(cachedCoupons && hasAuthFlag);
  // Lets a link land directly on a specific tab (e.g. Profile's points card -> "premier").
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(['kuponger', 'premier', 'arkiv'].includes(initialTab) ? initialTab : 'kuponger');
  const [showInfoSheet, setShowInfoSheet] = useState(false); // "How this page works" explainer
  const [userCoupons, setUserCoupons] = useState(cachedCoupons?.userCoupons || []);
  const [usedCoupons, setUsedCoupons] = useState(cachedCoupons?.usedCoupons || {});
  const [authUser, setAuthUser] = useState(canUseCache ? (cachedCoupons.authUser || 'cached') : undefined); // undefined = loading, null = guest
  const [couponsLoaded, setCouponsLoaded] = useState(canUseCache); // has the coupons snapshot delivered yet
  const [userPoints, setUserPoints] = useState(() => {
    try { return Number(JSON.parse(localStorage.getItem('pws_user_cache'))?.points) || 0; } catch { return 0; }
  });
  const [rewardsCatalog, setRewardsCatalog] = useState(cachedCoupons?.rewardsCatalog || []); // global coupons with a pointCost - redeemed, not auto-granted
  const [redeemingRewardId, setRedeemingRewardId] = useState(null);
  const [highlightedCouponId, setHighlightedCouponId] = useState(null);

  const [activeModalItem, setActiveModalItem] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);

  const [timerActive, setTimerActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const [activeCouponId, setActiveCouponId] = useState(null);

  const formatNordicDate = (isoDate) => {
    if (!isoDate) return '-';
    const [year, month, day] = isoDate.split('-');
    if (!year || !month || !day) return '-';
    return `${day}.${month}.${year}`;
  };

  function isExpired(dateStr) {
    if (!dateStr) return false;
    const [y, m, d] = dateStr.split("-");
    const expirationDate = new Date(y, m - 1, d, 23, 59, 59);
    return new Date() > expirationDate;
  }

  function formatTimer(sec) {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  }

  const openModal = (item, kind) => {
    setActiveModalItem({ ...item, kind });
    setTimeout(() => setModalVisible(true), 20);
  };

  const closeModal = () => {
    setModalVisible(false);
    setTimeout(() => {
      setActiveModalItem(null);
      setDragY(0);
    }, 280);
  };

  // Body scroll lock while modal open
  useEffect(() => {
    if (!activeModalItem) return;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [activeModalItem]);

  // Body scroll lock while the guest lock overlay is up
  useEffect(() => {
    if (authUser !== null) return;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [authUser]);

  // Escape key closes modal
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    if (activeModalItem) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeModalItem]);

  // Clear the just-redeemed coupon highlight after the pulse has had time to play
  useEffect(() => {
    if (!highlightedCouponId) return;
    const timer = setTimeout(() => setHighlightedCouponId(null), 2500);
    return () => clearTimeout(timer);
  }, [highlightedCouponId]);

  // Global mouse drag listeners
  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e) => {
      const delta = e.clientY - startY;
      if (delta > 0) setDragY(delta);
    };
    const onMouseUp = () => {
      setIsDragging(false);
      if (dragY > 140) { closeModal(); } else { setDragY(0); }
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, startY, dragY]);

  const handleDragStart = (clientY) => { setIsDragging(true); setStartY(clientY); };
  const handleDragMove = (clientY) => {
    if (!isDragging) return;
    const delta = clientY - startY;
    if (delta > 0) setDragY(delta);
  };
  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > 140) { closeModal(); } else { setDragY(0); }
  };


  // Load + sync per-user coupons
  useEffect(() => {
    let dbUserId = null;
    let channels = [];

    async function syncCoupons(userEmail) {
      // The database gives free coupons, hides deleted ones and stamps used_at
      // (sync_my_coupons, migration 024) - customers can't write grants themselves.
      await supabase.rpc('sync_my_coupons');
      const { data: globalRows } = await supabase.from('coupons_catalog').select('*');
      const globalCoupons = (globalRows || []).map(toCatalogShape);
      const { data: userRows } = await supabase.from('user_coupons').select('*, coupons_catalog(*)').eq('user_id', dbUserId);
      const couponsRows = userRows || [];

      setRewardsCatalog(globalCoupons.filter(gc => Number(gc.pointCost) > 0 && !gc.deleted));

      const isExpiredFn = (dateStr) => {
        if (!dateStr) return false;
        const [y, m, d] = dateStr.split("-");
        const expirationDate = new Date(y, m - 1, d, 23, 59, 59);
        return new Date() > expirationDate;
      };

      const coupons = couponsRows
        .filter(r => !r.deleted)
        .map(toCouponShape)
        .filter(coupon => !isExpiredFn(coupon.expiration))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

      const usedMap = {};
      coupons.forEach(c => {
        // A reward is "Brukt" once every purchased unit has been shown to staff
        // (claimCount catches up to usageCount) - not tied to maxUses at all, since
        // buying more (up to maxUses) just adds more unclaimed units to show later.
        const isUsed = isRewardGrant(c)
          ? getRewardClaimCount(c) >= getRewardPurchaseCount(c)
          : (c.used || (Number.parseInt(c.usageCount, 10) || 0) >= Math.max(1, Number.parseInt(c.maxUses, 10) || 1));
        if (isUsed) usedMap[c.id] = true;
      });

      setUserCoupons(coupons);
      setUsedCoupons(usedMap);
      setCouponsLoaded(true);
      try {
        localStorage.setItem('pws_coupons_cache', JSON.stringify({
          authUser: userEmail,
          userCoupons: coupons,
          usedCoupons: usedMap,
          rewardsCatalog: globalCoupons.filter(gc => Number(gc.pointCost) > 0 && !gc.deleted),
        }));
      } catch {}
    }

    async function handleAuthUser(user) {
      setAuthUser(user ? user.email?.toLowerCase?.() || 'logged-in' : null);
      channels.forEach(ch => supabase.removeChannel(ch));
      channels = [];
      if (!user) {
        setUserCoupons([]);
        setUsedCoupons({});
        setRewardsCatalog([]);
        setCouponsLoaded(true);
        localStorage.removeItem('pws_coupons_cache');
        return;
      }
      const userEmail = user.email.toLowerCase();
      const { data: userRow } = await supabase.from('users').select('id, points').eq('auth_user_id', user.id).maybeSingle();
      if (!userRow) { setCouponsLoaded(true); return; }
      dbUserId = userRow.id;
      setUserPoints(Number(userRow.points) || 0);

      await syncCoupons(userEmail);

      // Realtime: global catalog changes re-run the full sync (matches the
      // original onSnapshot-on-myCouponsFb trigger); points changes on this
      // user's own row update the points display directly.
      const catalogChannel = supabase
        .channel('coupons_catalog_changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'coupons_catalog' }, () => syncCoupons(userEmail))
        .subscribe();
      const pointsChannel = supabase
        .channel('own_points_changes')
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'users', filter: `id=eq.${userRow.id}` },
          (payload) => setUserPoints(Number(payload.new.points) || 0))
        .subscribe();
      channels = [catalogChannel, pointsChannel];
    }

    // getUser() (not just relying on onAuthStateChange's implicit "fire once
    // immediately" behavior) re-validates against Supabase directly - same
    // reasoning ProtectedRoute.jsx already uses. In the Capacitor Android
    // wrapper specifically, backgrounding the app can suspend the SDK's
    // session-rehydration mid-flight; when the WebView resumes, that first
    // synthetic event can get lost entirely, leaving this stuck thinking
    // nobody's logged in (shows the login-required modal) until a full app
    // restart. Calling getUser() directly here doesn't depend on that event
    // ever firing.
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled) handleAuthUser(data.user || null);
    });

    // INITIAL_SESSION is the SDK's own synthetic "here's the state at
    // subscribe-time" event - skipped here since getUser() above already
    // covers it; this listener is only for a REAL change (sign-in/out) while
    // this tab stays mounted. Without the skip, both paths would race to set
    // up their own Realtime channels and one pair would leak unremoved.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') return;
      handleAuthUser(session?.user || null);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      channels.forEach(ch => supabase.removeChannel(ch));
    };
  }, []);

  const markCouponAsUsed = useCallback(async (couponId) => {
    const coupon = userCoupons.find(c => c.id === couponId);
    if (coupon) {
      let shouldMarkUsed;
      let nextLocalFields;

      if (isRewardGrant(coupon)) {
        // A reward's "use" (Vis til ansatt) claims one already-purchased unit - it must
        // NOT touch usageCount, or showing it to staff would burn a point-purchase slot.
        const purchaseCount = getRewardPurchaseCount(coupon);
        const nextClaimCount = Math.min(purchaseCount, getRewardClaimCount(coupon) + 1);
        shouldMarkUsed = nextClaimCount >= purchaseCount;
        nextLocalFields = { claimCount: nextClaimCount };
      } else {
        const maxUses = Math.max(1, Number.parseInt(coupon.maxUses, 10) || 1);
        const nextUsageCount = Math.min(maxUses, (Number.parseInt(coupon.usageCount, 10) || 0) + 1);
        shouldMarkUsed = nextUsageCount >= maxUses;
        nextLocalFields = { usageCount: nextUsageCount, used: shouldMarkUsed };
      }

      const existingUsedAt = coupon.usedAt;
      const usedAt = shouldMarkUsed ? (existingUsedAt || new Date().toISOString()) : existingUsedAt;
      if (shouldMarkUsed) nextLocalFields.usedAt = usedAt;

      setUserCoupons((prev) => prev.map((c) => (
        c.id === couponId ? { ...c, ...nextLocalFields } : c
      )));
      setUsedCoupons(prev => ({ ...prev, [couponId]: shouldMarkUsed }));

      // Same counting in the database (use_coupon, migration 024); the local
      // update above is only so the card changes right away.
      await supabase.rpc('use_coupon', { p_coupon_id: coupon.id });
    }

    setTimerActive(false);
    setActiveCouponId(null);
    setTimeLeft(15);
    localStorage.removeItem("activeCouponTimer");
  }, [userCoupons]);

  // Resume in-progress redeem timer after reload
  useEffect(() => {
    const timerData = localStorage.getItem("activeCouponTimer");
    if (timerData) {
      const { id, endTime } = JSON.parse(timerData);
      const diff = Math.floor((endTime - Date.now()) / 1000);
      if (diff > 0) {
        setActiveCouponId(id);
        setTimerActive(true);
        setTimeLeft(diff);
      } else {
        markCouponAsUsed(id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redeem timer countdown
  useEffect(() => {
    if (timerActive && activeCouponId) {
      if (timeLeft <= 0) {
        markCouponAsUsed(activeCouponId);
        return;
      }
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timerActive, timeLeft, activeCouponId, markCouponAsUsed]);

  async function handleUseCoupon(coupon) {
    if (timerActive) return;
    if (isExpired(coupon.expiration)) return;
    if (!isCouponAllowedToday(coupon)) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: userRow } = await supabase.from('users').select('id').eq('auth_user_id', user.id).maybeSingle();
      if (userRow) {
        // Running total is just `count(*)` on this table now - no separate
        // counter doc to keep in sync (that was Firestore-only overhead).
        await supabase.from('coupon_usage_log').insert({ coupon_id: coupon.id, user_id: userRow.id });
      }
    }

    const endTime = Date.now() + 15 * 1000;
    setActiveCouponId(coupon.id);
    setTimerActive(true);
    setTimeLeft(15);
    localStorage.setItem("activeCouponTimer", JSON.stringify({ id: coupon.id, endTime }));
  }

  // Redeem a point-cost reward: atomically deduct points and grant the coupon, so a
  // double-click (or two tabs) can't spend the same points twice or redeem it twice.
  // The atomicity itself lives in the redeem_reward Postgres function
  // (migration 024): it takes the cost and limits from coupons_catalog and the
  // customer from the login, never from the phone.
  async function handleRedeemReward(reward) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || redeemingRewardId) return false;

    setRedeemingRewardId(reward.id);
    try {
      const { data: newUsageCount, error } = await supabase.rpc('redeem_reward', { p_coupon_id: reward.id });
      if (error) throw error;

      // The personal coupons list only re-syncs when the global catalog changes, so
      // reflect the new grant locally right away instead of waiting for that trigger.
      setUserCoupons((prev) => {
        const idx = prev.findIndex((c) => c.id === reward.id);
        const updated = { ...reward, used: false, usageCount: newUsageCount, redeemedWithPoints: true };
        if (idx === -1) return [...prev, updated];
        const next = [...prev];
        next[idx] = updated;
        return next;
      });
      setRedeemingRewardId(null);
      return true;
    } catch (error) {
      toast.error(error.message || t('toastRedeemFailed'));
    }
    setRedeemingRewardId(null);
    return false;
  }

  function handleCouponClick(coupon) {
    if (navigator.vibrate) navigator.vibrate(30);
    if (isExpired(coupon.expiration)) return;

    const isUnlimited = coupon.unlimited || coupon.expiration === null;
    const isUsed = usedCoupons[coupon.id];
    const isOtherTimerActive = timerActive && activeCouponId !== coupon.id;

    if ((isUnlimited || !isUsed) && !isOtherTimerActive) {
      // An owned reward instance in "Mine kuponger" is a voucher to claim (Vis til
      // ansatt), not something to buy again - it always goes through the coupon-style
      // branch of getModalContent. The reward-purchase branch is only reachable from
      // the "Tilgjengelige rewards" catalog (see openModal(reward, 'reward') there).
      openModal(coupon, 'coupon');
    }
  }

  // A reward stays in the redeemable catalog until its per-user usage count actually
  // hits the admin-configured max - it's no longer archived after a single use.
  const availableRewards = rewardsCatalog.filter((reward) => {
    const maxUses = getRewardMaxUses(reward);
    const grant = userCoupons.find((c) => c.id === reward.id);
    const usageCount = grant ? (Number.parseInt(grant.usageCount, 10) || 0) : 0;
    return usageCount < maxUses;
  });

  const activeCoupons = userCoupons.filter(c => !usedCoupons[c.id]);
  const usedUserCoupons = userCoupons
    .filter(c => usedCoupons[c.id])
    .map((coupon) => ({ ...coupon, _retention: getUsedCouponRetention(coupon) }))
    .filter((coupon) => coupon._retention.shouldShow);

  // Build shared modal content from the active item
  const getModalContent = () => {
    if (!activeModalItem) return null;

    if (activeModalItem.kind === 'reward') {
      const reward = activeModalItem;
      const cost = Number(reward.pointCost) || 0;
      const isUnlimitedReward = reward.unlimited || reward.expiration === null || !reward.expiration;
      const maxUses = getRewardMaxUses(reward);
      const grant = userCoupons.find((c) => c.id === reward.id);
      const usageCount = grant ? (Number.parseInt(grant.usageCount, 10) || 0) : 0;
      const usesLeft = Math.max(0, maxUses - usageCount);
      return {
        icon: <FiGift size={22} />,
        iconClass: 'yellow',
        title: reward.title,
        tag: { text: t('pointsCost', { cost }), cls: 'yellow' },
        description: reward.description,
        meta: [
          { icon: <FiStar />, label: t('metaPointsCost'), value: t('pointsCost', { cost }) },
          { icon: <FiCalendar />, label: t('metaExpires'), value: isUnlimitedReward ? t('noExpiryDate') : formatNordicDate(reward.expiration) },
          ...(Number.isFinite(maxUses) && maxUses > 1 ? [{ icon: <FiTag />, label: t('metaUse'), value: t('usageProgress', { usageCount, maxUses, usesLeft }) }] : []),
        ],
        isReward: true,
        alreadyRedeemed: usesLeft <= 0,
        canAfford: userPoints >= cost,
      };
    }

    // Re-derive from the live userCoupons list, not the activeModalItem snapshot taken
    // when the modal opened - otherwise claimCount/usageCount stay frozen at whatever
    // they were on open, and the "Vis til ansatt" button never updates once the claim
    // timer finishes (it'd keep reading the pre-claim state and stay clickable).
    const coupon = userCoupons.find((c) => c.id === activeModalItem.id) || activeModalItem;
    const isReward = isRewardGrant(coupon);
    // For a reward voucher, "Bruk" tracks claims (Vis til ansatt) against how many
    // you've purchased with points - not against maxUses, which only caps purchases.
    const maxUses = isReward ? getRewardPurchaseCount(coupon) || 1 : Math.max(1, Number.parseInt(coupon.maxUses, 10) || 1);
    const usageCountRaw = isReward ? getRewardClaimCount(coupon) : Number.parseInt(coupon.usageCount, 10) || 0;
    const usageCount = Math.min(maxUses, usageCountRaw);
    const isUsed = isReward
      ? getRewardClaimCount(coupon) >= getRewardPurchaseCount(coupon)
      : (usedCoupons[coupon.id] || usageCountRaw >= maxUses);
    const repeatDayLabels = Array.isArray(coupon.repeatDays)
      ? coupon.repeatDays
        .map((value) => t(WEEKDAY_OPTIONS.find((day) => day.value === value)?.labelKey) || value)
        .join(', ')
      : '';
    const canUseToday = isCouponAllowedToday(coupon);
    const isUnlimited = coupon.unlimited || coupon.expiration === null || !coupon.expiration;
    // getCouponStatus (and the isUsed field below) treat "no expiration" as "never show
    // Brukt" - that's intentional for a real coupon, but a reward voucher's claimed
    // status has nothing to do with its expiration date, so it must bypass that gate.
    const status = isReward
      ? (isUsed ? { text: t('statusUsed'), cardCls: 'used-tag', tagCls: 'gray' } : { text: t('statusActive'), cardCls: 'active', tagCls: 'green' })
      : getCouponStatus(t, coupon, isUsed);
    const usesLeft = Math.max(0, maxUses - usageCount);

    return {
      icon: <FiGift size={22} />,
      iconClass: coupon.redeemedWithPoints ? 'yellow' : 'green',
      title: coupon.title,
      tag: { text: status.text, cls: status.tagCls },
      description: coupon.description,
      meta: [
        { icon: <FiCalendar />, label: t('metaExpires'), value: isUnlimited ? t('noExpiryDate') : formatNordicDate(coupon.expiration) },
        ...(coupon.startTime && coupon.endTime ? [{ icon: <FiCalendar />, label: t('metaTime'), value: t('timeRange', { start: coupon.startTime, end: coupon.endTime }) }] : []),
        ...(coupon.priceBadge ? [{ icon: <FiCreditCard />, label: t('metaPrice'), value: coupon.priceBadge }] : []),
        { icon: <FiTag />, label: isReward ? t('metaShownToStaff') : t('metaUse'), value: t('usageProgress', { usageCount, maxUses, usesLeft }) },
        ...(coupon.repeatWeekly && repeatDayLabels ? [{ icon: <FiCalendar />, label: t('metaApplies'), value: repeatDayLabels }] : []),
      ],
      isCoupon: true,
      isUsed: isReward ? isUsed : (isUsed && !isUnlimited),
      canUseToday,
    };
  };

  const modalContent = getModalContent();

  const modal = activeModalItem && typeof document !== 'undefined'
    ? createPortal(
        <div
          className={`coupons-modal-overlay ${modalVisible ? 'visible' : 'hidden'}`}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
          style={{ opacity: modalVisible ? Math.max(0, 1 - dragY / 320) : 0 }}
        >
          <CloseOnBack onClose={closeModal} />
          <div
            className={`coupons-modal-sheet ${modalVisible ? 'visible' : 'hidden'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                transform: `translateY(${dragY}px)`,
                transition: isDragging ? 'none' : 'transform 0.28s ease'
              }}
              onTouchStart={(e) => { e.preventDefault(); handleDragStart(e.touches[0].clientY); }}
              onTouchMove={(e) => { e.preventDefault(); handleDragMove(e.touches[0].clientY); }}
              onTouchEnd={handleDragEnd}
              onMouseDown={(e) => handleDragStart(e.clientY)}
            >
              <div className="coupons-modal-handle" />
              <div className="coupons-modal-header">
                <div className="coupons-modal-header-left">
                  <div className={`coupons-modal-icon ${modalContent.iconClass}`}>{modalContent.icon}</div>
                  <div>
                    <div className="coupons-modal-title">{modalContent.title}</div>
                  </div>
                </div>
                <button className="coupons-modal-close" onClick={closeModal} aria-label={t('close')}>
                  <FiX size={16} />
                </button>
              </div>

              <div className="coupons-modal-body">
                <span className={`coupons-modal-tag coupons-modal-tag-${modalContent.tag.cls}`}>{modalContent.tag.text}</span>

                <p className="coupons-modal-description">{modalContent.description}</p>

                <div className="coupons-modal-meta">
                  {modalContent.meta.map((row) => (
                    <div className="coupons-meta-row" key={row.label}>
                      {row.icon}
                      <span>{row.label}</span>
                      <strong>{row.value}</strong>
                    </div>
                  ))}
                </div>

                {modalContent.isReward ? (
                  modalContent.alreadyRedeemed ? (
                    <button className="coupons-modal-btn" disabled>{t('alreadyRedeemed')}</button>
                  ) : !modalContent.canAfford ? (
                    <button className="coupons-modal-btn" disabled>{t('notEnoughPoints')}</button>
                  ) : (
                    <button
                      className="coupons-modal-btn"
                      disabled={redeemingRewardId === activeModalItem.id}
                      onClick={async () => {
                        const rewardId = activeModalItem.id;
                        const success = await handleRedeemReward(activeModalItem);
                        closeModal();
                        if (success) {
                          setActiveTab('kuponger');
                          setHighlightedCouponId(rewardId);
                        }
                      }}
                    >
                      {redeemingRewardId === activeModalItem.id ? t('redeeming') : t('redeem')}
                    </button>
                  )
                ) : modalContent.isCoupon ? (
                  <>
                    {timerActive && activeCouponId === activeModalItem.id ? (
                      <div className="coupons-modal-timer">{t('usableIn', { time: formatTimer(timeLeft) })}</div>
                    ) : modalContent.canUseToday === false ? (
                      <button className="coupons-modal-btn" disabled>{t('cannotUseToday')}</button>
                    ) : modalContent.isUsed ? (
                      <button className="coupons-modal-btn" disabled>{t('alreadyUsed')}</button>
                    ) : (
                      <button className="coupons-modal-btn" onClick={() => { if (navigator.vibrate) navigator.vibrate(30); handleUseCoupon(activeModalItem); }}>
                        {t('showToStaff')}
                      </button>
                    )}
                  </>
                ) : (
                  <button className="coupons-modal-btn" onClick={modalContent.onPrimary}>
                    {modalContent.primaryLabel}
                  </button>
                )}
                <button className="coupons-modal-btn-ghost" onClick={closeModal}>{t('close')}</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div className="coupons-wrapper">
      <div className={authUser === null ? 'coupons-page-lock' : undefined}>
      <div className={authUser === null ? 'coupons-page-lock-content' : undefined}>
        <div className="coupons-header-row">
          <div className="coupons-tabs">
            <div
              className={`coupons-tab ${activeTab === 'kuponger' ? 'active' : ''}`}
              onClick={() => setActiveTab('kuponger')}
            >
              {t('tabCoupons')}
              {activeCoupons.length > 0 && <span className="coupons-tab-badge">{activeCoupons.length}</span>}
            </div>
            <div
              className={`coupons-tab ${activeTab === 'premier' ? 'active' : ''}`}
              onClick={() => setActiveTab('premier')}
            >
              {t('tabRewards')}
              {availableRewards.length > 0 && <span className="coupons-tab-badge">{availableRewards.length}</span>}
            </div>
            <div
              className={`coupons-tab coupons-tab--icon ${activeTab === 'arkiv' ? 'active' : ''}`}
              onClick={() => setActiveTab('arkiv')}
              role="button"
              aria-label={t('tabArchive')}
              title={t('tabArchive')}
            >
              <FiArchive size={16} />
            </div>
          </div>
          <button className="coupons-info-btn" onClick={() => setShowInfoSheet(true)} aria-label={t('howCouponsWorkTitle')} title={t('howCouponsWorkTitle')}>
            <FiInfo size={18} />
          </button>
        </div>

        {activeTab === 'premier' && (
          <div className="coupons-tab-content">
            <div className="coupons-points-banner">
              <div className="coupons-points-label">{t('yourPoints')}</div>
              <div className="coupons-points-value">{(Number(userPoints) || 0).toFixed(2)}</div>
              <div className="coupons-points-sub">{t('availableForRewards')}</div>
            </div>
            {authUser === null ? (
              <div className="coupons-empty-state">
                <FiGift className="coupons-empty-icon" />
                <h3>{t('loginForRewardsTitle')}</h3>
                <p>{t('loginForRewardsDesc')}</p>
              </div>
            ) : availableRewards.length === 0 ? (
              <div className="coupons-empty-state">
                <FiGift className="coupons-empty-icon" />
                <h3>{t('noRewardsTitle')}</h3>
                <p>{t('noRewardsDesc')}</p>
              </div>
            ) : (
              <>
                <div className="coupons-section-label">{t('availableRewardsLabel')}</div>
                <div className="reward-grid">
                {availableRewards.map((reward) => {
                  const cost = Number(reward.pointCost) || 0;
                  return (
                    <div
                      key={reward.id}
                      className="reward-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => openModal(reward, 'reward')}
                      onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') openModal(reward, 'reward'); }}
                    >
                      <FiStar size={70} strokeWidth={1} className="reward-card-star reward-card-star--1" />
                      <FiStar size={80} strokeWidth={1} className="reward-card-star reward-card-star--2" />
                      <div className="reward-cost"><FiStar size={11} /> {t('pointsCost', { cost })}</div>
                      <div className="tilbud-inner">
                        <div className="tilbud-badge yellow"><FiGift size={20} /></div>
                        <div className="tilbud-text">
                          <h3 className="reward-title">{reward.title}</h3>
                        </div>
                        <FiChevronRight size={16} className="tilbud-arrow" />
                      </div>
                      <div className="coupon-strip">
                        <div className="coupon-strip-left"><FiGift size={13} /> {t('activeRewardLabel')}</div>
                        <span className="coupon-strip-code">{t('notRedeemedYet')}</span>
                      </div>
                    </div>
                  );
                })}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'kuponger' && (
          <div className="coupons-tab-content">
            {authUser === undefined || (authUser !== null && !couponsLoaded) ? (
              <>
                <div className="coupons-section-label">{t('myCoupons')}</div>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="tilbud-card tilbud-card--coupon tilbud-card--skeleton" aria-hidden="true">
                    <div className="tilbud-inner">
                      <div className="tilbud-badge tilbud-skeleton-shape" />
                      <div className="tilbud-text">
                        <div className="tilbud-skeleton-line tilbud-skeleton-line--tag" />
                        <div className="tilbud-skeleton-line tilbud-skeleton-line--title" />
                      </div>
                    </div>
                    <div className="coupon-strip">
                      <div className="tilbud-skeleton-line tilbud-skeleton-line--strip" />
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
            {authUser === null && (
              <div className="coupons-empty-state" style={{ marginBottom: 16 }}>
                <FiGift className="coupons-empty-icon" />
                <h3>{t('loginForCouponsTitle')}</h3>
                <p>{t('loginForCouponsDesc')}</p>
              </div>
            )}
            {userCoupons.length === 0 && authUser !== null ? (
              <div className="coupons-empty-state">
                <FiGift className="coupons-empty-icon" />
                <h3>{t('noCouponsTitle')}</h3>
                <p>{t('noCouponsDesc')}</p>
              </div>
            ) : authUser !== null ? (
              <>
                {activeCoupons.length > 0 && (
                  <>
                    <div className="coupons-section-label">{t('myCoupons')}</div>
                    {activeCoupons.map((coupon) => {
                      const status = getCouponStatus(t, coupon, false);
                      const isUnlimited = coupon.unlimited || coupon.expiration === null || !coupon.expiration;
                      return (
                        <div
                          key={coupon.id}
                          className={`tilbud-card tilbud-card--coupon${coupon.redeemedWithPoints ? ' tilbud-card--reward' : ''}${highlightedCouponId === coupon.id ? ' tilbud-card--pulse' : ''}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleCouponClick(coupon)}
                          onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCouponClick(coupon); }}
                        >
                          <div className="tilbud-inner">
                            <div className={`tilbud-badge ${coupon.redeemedWithPoints ? 'yellow' : 'green'}`}><FiGift size={22} /></div>
                            <div className="tilbud-text">
                              <div className="tilbud-tag-row">
                                <span className={`tilbud-tag tilbud-tag--${status.tagCls}`}>{t('couponTag')}</span>
                                {coupon.redeemedWithPoints && (
                                  <span className="tilbud-tag tilbud-tag--yellow" title="Reward"><FiStar size={10} /></span>
                                )}
                              </div>
                              <h3>{coupon.title}</h3>
                            </div>
                            <FiChevronRight size={16} className="tilbud-arrow" />
                          </div>
                          <div className="coupon-strip">
                            <div className="coupon-strip-left"><FiCreditCard size={13} /> {coupon.redeemedWithPoints ? t('activeRewardLabel') : t('activeCouponLabel')}</div>
                            <span className="coupon-strip-code">{isUnlimited ? t('noExpiryDate') : t('expiresOn', { date: formatNordicDate(coupon.expiration) })}</span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            ) : null}
              </>
            )}
          </div>
        )}

        {activeTab === 'arkiv' && (
          <div className="coupons-tab-content">
            {usedUserCoupons.length === 0 ? (
              <div className="coupons-empty-state">
                <FiArchive className="coupons-empty-icon" />
                <h3>{t('noUsedTitle')}</h3>
                <p>{t('noUsedDesc')}</p>
              </div>
            ) : (
              <>
                <div className="coupons-section-label">{t('usedSectionLabel')}</div>
                {usedUserCoupons.map((coupon) => {
                  const daysLeft = coupon._retention?.daysLeft || USED_COUPON_RETENTION_DAYS;
                  const dayUnit = t(daysLeft === 1 ? 'dayUnitSingular' : 'dayUnitPlural');
                  return (
                    <div key={coupon.id} className={`tilbud-card tilbud-card--used${coupon.redeemedWithPoints ? ' tilbud-card--reward' : ''}`}>
                      <div className="coupon-removal-overlay">{t('removedIn', { days: daysLeft, unit: dayUnit })}</div>
                      <div className="tilbud-inner">
                        <div className={`tilbud-badge ${coupon.redeemedWithPoints ? 'yellow' : 'green'}`}><FiGift size={22} /></div>
                        <div className="tilbud-text">
                          <div className="tilbud-tag-row">
                            <span className="tilbud-tag tilbud-tag--gray">{t('statusUsed')}</span>
                            {coupon.redeemedWithPoints && (
                              <span className="tilbud-tag tilbud-tag--yellow" title="Reward"><FiStar size={10} /></span>
                            )}
                          </div>
                          <h3>{coupon.title}</h3>
                        </div>
                      </div>
                      <div className="coupon-strip">
                        <div className="coupon-strip-left"><FiCreditCard size={13} /> {coupon.redeemedWithPoints ? t('usedRewardLabel') : t('usedCouponLabel')}</div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

      </div>
      </div>
      {authUser === null && (
        <div className="coupons-page-lock-overlay">
          <div className="coupons-page-lock-card">
            <div className="coupons-page-lock-icon"><FiLock size={24} /></div>
            <h3>{t('loginToContinueTitle')}</h3>
            <p>{t('loginToContinueDesc')}</p>
            <button type="button" className="coupons-page-lock-btn" onClick={() => navigate('/LogIn')}>
              {t('loginButton')}
            </button>
          </div>
        </div>
      )}
      </div>
      {modal}
      {showInfoSheet && (
        <BottomSheet
          icon={<FiInfo size={20} />}
          iconColor="blue"
          title={t('howCouponsWorkTitle')}
          onClose={() => setShowInfoSheet(false)}
        >
          <div className="coupons-info-block">
            <div className="coupons-info-block-icon"><FiStar size={16} /></div>
            <div>
              <div className="coupons-info-block-title">{t('infoPointsTitle')}</div>
              <div className="coupons-info-block-text">
                {t('infoPointsText', { percent: pointsPercent })}
              </div>
            </div>
          </div>
          <div className="coupons-info-block">
            <div className="coupons-info-block-icon coupons-info-block-icon--purple"><FiGift size={16} /></div>
            <div>
              <div className="coupons-info-block-title">{t('infoRewardsTitle')}</div>
              <div className="coupons-info-block-text">
                {t('infoRewardsText')}
              </div>
            </div>
          </div>
          <div className="coupons-info-block">
            <div className="coupons-info-block-icon coupons-info-block-icon--green"><FiTag size={16} /></div>
            <div>
              <div className="coupons-info-block-title">{t('infoCouponsTitle')}</div>
              <div className="coupons-info-block-text">
                {t('infoCouponsText')}
              </div>
            </div>
          </div>
        </BottomSheet>
      )}
    </>
  );
}
